import { useLocation, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import FluxiLogo from "../assets/svg/FluxiLogo.svg";
import metaLogo from "../assets/images/meta-logo.png";
import { CheckCircle2, XCircle, Link2 } from "lucide-react";

const MetaCallbackPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const isSuccess = status === "connected";
  const provider = resolveOAuthProvider(location.pathname);

  useEffect(() => {
    window.opener?.postMessage(
      {
        type: `fluxi:${provider.messageKey}-oauth`,
        status,
      },
      window.location.origin
    );

    if (isSuccess) {
      const timeout = window.setTimeout(() => window.close(), 900);
      return () => window.clearTimeout(timeout);
    }
  }, [isSuccess, provider.messageKey, status]);

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {/* Logos */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <img src={FluxiLogo} alt="Fluxi" className="h-8" />
          <Link2 className={`w-6 h-6 ${isSuccess ? "text-green-500" : "text-red-400"}`} />
          <ProviderMark provider={provider} />
        </div>

        {/* Status icon */}
        <div className="flex justify-center mb-4">
          {isSuccess ? (
            <CheckCircle2 className="w-16 h-16 text-green-500" />
          ) : (
            <XCircle className="w-16 h-16 text-red-400" />
          )}
        </div>

        {/* Message */}
        {isSuccess ? (
          <>
            <h1 className="text-xl font-bold text-gray-800 mb-2">
              Cuenta conectada exitosamente
            </h1>
            <p className="text-sm text-gray-500 mb-8">
              Tu cuenta de {provider.label} ha sido conectada a Fluxi. Puedes cerrar esta ventana y continuar en la aplicación.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-gray-800 mb-2">
              Error al conectar
            </h1>
            <p className="text-sm text-gray-500 mb-8">
              No se pudo conectar tu cuenta de {provider.label}. Por favor cierra esta ventana e intenta nuevamente.
            </p>
          </>
        )}

        {/* Close button */}
        <button
          className={`w-full px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
            isSuccess
              ? "bg-fluxi hover:bg-[#006AD4]"
              : "bg-gray-600 hover:bg-gray-700"
          }`}
          onClick={() => window.close()}
        >
          Cerrar ventana
        </button>
      </div>
    </div>
  );
};

type OAuthProvider = {
  label: string;
  messageKey: "meta" | "tiktok" | "google-ads";
  badgeClassName: string;
};

function resolveOAuthProvider(pathname: string): OAuthProvider {
  if (pathname.includes("google-ads")) {
    return {
      label: "Google Ads",
      messageKey: "google-ads",
      badgeClassName: "border-emerald-100 bg-emerald-50 text-emerald-700",
    };
  }

  if (pathname.includes("tiktok")) {
    return {
      label: "TikTok Ads",
      messageKey: "tiktok",
      badgeClassName: "border-gray-200 bg-gray-900 text-white",
    };
  }

  return {
    label: "Meta Ads",
    messageKey: "meta",
    badgeClassName: "border-blue-100 bg-blue-50 text-[#2b5ce6]",
  };
}

function ProviderMark({ provider }: { provider: OAuthProvider }) {
  if (provider.messageKey === "meta") {
    return <img src={metaLogo} alt="Meta" className="h-8 object-contain" />;
  }

  return (
    <span className={`inline-flex h-8 items-center rounded-full border px-3 text-xs font-bold ${provider.badgeClassName}`}>
      {provider.label}
    </span>
  );
}

export default MetaCallbackPage;
